import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Menu, MenuItem, ListItemText, Typography } from '@mui/material';
import { AI_PROVIDERS, getAiProvider, setAiProvider } from '../../utils/aiProvider';

/**
 * #106: lets the user pick the AI provider / source (OpenRouter, or the server
 * default). The choice is stored in localStorage and sent as the
 * `X-LLM-Provider` header; switching re-fetches the model list for that
 * source. Pair with the key entry (ApiKeySettings) for your own key.
 *
 * #1038: no longer owns its own trigger button/icon — the caller (ChatInterface's
 * burger-menu row) opens this menu imperatively via the ref, anchored to
 * whichever element the user clicked, so the whole row is the control rather
 * than just an embedded icon. `onChange` reports the chosen provider id back
 * up so the caller's menu row can show the active source.
 */
// The JSDoc below has to sit directly on a function DECLARATION, not on the
// `const ProviderSelector = forwardRef(...)` assignment that wraps it — a JSDoc
// above that `const` attaches to the variable statement, not to the render
// function nested inside its initializer, so the props/ref types never reach
// forwardRef's inference. Hoisting the render function out (as CommentComposer
// does, #968 Phase B) is what makes the JSDoc actually apply.
/**
 * @param {{ onChange?: (id: string) => void }} props
 * @param {React.ForwardedRef<{ open: (anchorElement: any) => void }>} ref
 */
function ProviderSelectorImpl({ onChange }, ref) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [provider, setProvider] = useState(getAiProvider());

  useImperativeHandle(ref, () => ({
    open: (anchorElement) => setAnchorEl(anchorElement),
  }), []);

  const close = () => setAnchorEl(null);

  const choose = (id) => {
    setAiProvider(id);          // persists + dispatches the change event (models refetch)
    setProvider(id);
    onChange?.(id);
    close();
  };

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
      <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
        AI source
      </Typography>
      {AI_PROVIDERS.map((p) => (
        <MenuItem
          key={p.id || 'default'}
          selected={p.id === provider}
          onClick={() => choose(p.id)}
          sx={{ fontSize: '0.875rem' }}
        >
          <ListItemText primary={p.label} />
        </MenuItem>
      ))}
    </Menu>
  );
}

const ProviderSelector = forwardRef(ProviderSelectorImpl);

export default ProviderSelector;
